import React, { useState } from 'react';
import { PlayerProps } from '../types';
import './Player.css';

/**
 * Player component that renders WR as "x" icon and CB as "o" icon
 * Uses absolute positioning within the simulation container
 * Supports hover tooltips for position descriptions
 * Requirements: 2.1, 2.2, 2.4
 */
const Player: React.FC<PlayerProps> = ({ 
  position, 
  playerType, 
  isAnimating,
  positionId,
  label,
  hoverDescription,
  onClick,
  isSelected = false,
  badge,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const isWR = playerType === 'WR';
  const isClickable = typeof onClick === 'function';

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isClickable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick!();
    }
  };

  return (
    <div
      className={`player player-${playerType.toLowerCase()} ${isAnimating ? 'animating' : ''} ${isClickable ? 'clickable' : ''} ${isSelected ? 'selected' : ''}`}
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
      }}
      role={isClickable ? 'button' : 'img'}
      aria-label={isClickable ? `Select ${positionId ?? playerType}` : (isWR ? 'Wide Receiver' : 'Cornerback')}
      aria-pressed={isClickable ? isSelected : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      data-testid={`player-${playerType.toLowerCase()}${positionId ? `-${positionId}` : ''}`}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span className="player-icon">
        {isWR ? '×' : '○'}
      </span>
      <span className="player-label">{label ?? playerType}</span>
      {badge && <span className="player-badge">{badge}</span>}

      {hoverDescription && showTooltip && (
        <div 
          className="player-tooltip"
          role="tooltip"
          aria-hidden={!showTooltip}
        >
          <div className="player-tooltip-title">{hoverDescription.title}</div>
          <div className="player-tooltip-description">{hoverDescription.description}</div>
        </div>
      )}
    </div>
  );
};

export default Player;
